import { registerServiceWorker } from './sw-register.js';
import { createUpdateChecker, UpdateChecker } from './update-checker.js';
import { createRouter } from './router.js';
import { basePath } from './base.js';
import './install-prompt.js';
import { comingSoonScreen } from './screens/coming-soon.js';
import { installScreen } from './screens/install.js';
import { loginScreen } from './screens/login.js';
import { homeScreen } from './screens/home.js';
import { accountScreen } from './screens/account.js';
import { settingsScreen } from './screens/settings.js';
import { recurringScreen } from './screens/recurring.js';
import { budgetScreen } from './screens/budget.js';
import { initAutoSync } from './auto-sync.js';
import { createTabBar } from './tabbar.js';

// --- Diagnostic logging (sends to server) ---

const _logQueue: { level: string; msg: string }[] = [];
let _logTimer: ReturnType<typeof setTimeout> | null = null;

function dlog(msg: string, level = 'info') {
  console.log(`[diag] ${msg}`);
  _logQueue.push({ level, msg: `[${new Date().toISOString()}] ${msg}` });
  if (!_logTimer) {
    _logTimer = setTimeout(flushLogs, 500);
  }
}

function flushLogs() {
  _logTimer = null;
  if (_logQueue.length === 0) return;
  const batch = _logQueue.splice(0);
  fetch(basePath() + 'api/client-log', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(batch),
  }).catch(() => {});
}

// Catch uncaught errors
window.addEventListener('error', (e) => {
  dlog(`UNCAUGHT: ${e.message} at ${e.filename}:${e.lineno}:${e.colno}`, 'error');
  setTimeout(flushLogs, 0);
});
window.addEventListener('unhandledrejection', (e) => {
  dlog(`UNHANDLED_PROMISE: ${e.reason}`, 'error');
  setTimeout(flushLogs, 0);
});

dlog(`main.ts loaded | href=${location.href} | hash=${location.hash} | standalone=${window.matchMedia('(display-mode: standalone)').matches} | navigator.standalone=${(navigator as any).standalone}`);

// --- Entry routing ---------------------------------------------------------
// Three public-facing "faces", chosen once on load:
//   • coming-soon — the public page at "/" for logged-out browser visitors.
//   • install     — the "/install" page: PWA install prompt + how-to + a way in.
//   • app         — the hash-routed SPA (login → home → account / settings).
//
// Decision order:
//   1. standalone (installed PWA)  → app   (installed users are past the gate)
//   2. URL carries an app #hash    → app   (explicit entry, e.g. install page's "continue in browser")
//   3. path is "/install"          → install page
//   4. bare "/"                    → app if the session cookie is still valid, else coming-soon
// The app is never rendered on the coming-soon page, so logged-out visitors
// never see the login form. "/install" is reachable via the coming-soon
// easter egg (tap the name 10×) or a direct link.

function isStandaloneMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;
}

/** Path segment after the mount base, e.g. "install" for "/install". */
function currentPage(): string {
  return location.pathname.slice(basePath().length).replace(/\/+$/, '');
}

/** Raw session probe — not the api client, which would redirect to #login on 401. */
async function hasValidSession(): Promise<boolean> {
  try {
    const res = await fetch(basePath() + 'api/settings', { credentials: 'include' });
    return res.ok;
  } catch {
    return false;
  }
}

function goToInstall(): void { location.href = basePath() + 'install'; }
function enterApp(): void { location.href = basePath() + '#login'; }

async function routeEntry(): Promise<void> {
  const hasAppHash = location.hash.length > 1;
  if (isStandaloneMode() || hasAppHash) {
    dlog('Entry → app (standalone or deep-link hash)');
    bootApp();
    return;
  }
  if (currentPage() === 'install') {
    dlog('Entry → install page');
    document.body.appendChild(installScreen({ onOpenInApp: enterApp }));
    return;
  }
  if (await hasValidSession()) {
    dlog('Entry → app (valid session)');
    bootApp();
    return;
  }
  dlog('Entry → coming-soon (logged out)');
  document.body.appendChild(comingSoonScreen({ onEasterEgg: goToInstall }));
}

// Register service worker (needed for the install prompt to fire).
registerServiceWorker('./sw.js');

routeEntry();

function bootApp() {
  dlog('bootApp() START');
  const app = document.getElementById('app');
  dlog(`#app element: ${app ? 'found' : 'MISSING'} | classes: ${app?.className}`);
  if (app) app.classList.remove('hidden');
  dlog(`#app after unhide: classes=${app?.className} | display=${app ? getComputedStyle(app).display : 'N/A'}`);

  const container = document.getElementById('screen-container');
  dlog(`#screen-container: ${container ? 'found' : 'MISSING'}`);
  if (!container) {
    dlog('FATAL: #screen-container not found — aborting', 'error');
    setTimeout(flushLogs, 0);
    return;
  }

  // Screens: Home, Budget, Recurring, Settings (the tab bar), and Account off Home. No tab bar: Home's gear
  // opens Settings, an account row opens its Account screen, back returns Home.
  const router = createRouter(container);
  router.register('login', loginScreen);
  router.register('home', homeScreen);
  router.register('account', accountScreen);
  router.register('settings', settingsScreen);
  router.register('recurring', recurringScreen);
  router.register('budget', budgetScreen);

  // Tab bar under the scroll area, inside the app shell (a flex column).
  app?.appendChild(createTabBar());

  window.addEventListener('routechange', (e: Event) => {
    dlog(`routechange → ${(e as CustomEvent).detail.route}`);
    container.scrollTop = 0;
  });

  if (!location.hash || location.hash === '#') {
    location.hash = '#home';
  }
  dlog(`Starting router with hash=${location.hash}`);
  router.start();

  dlog('bootApp() DONE');

  // Background auto-sync: checks on boot, route change, and app resume.
  initAutoSync();

  // One-time update check
  checkForUpdate();

  // Re-check when app resumes from background
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  });

  // Allow other parts of the app (e.g. settings) to trigger the update popup
  window.addEventListener('mt-check-update', () => {
    checkForUpdate();
  });

  console.log('MoneyTrack loaded');
}

let updatePopupVisible = false;

async function checkForUpdate() {
  if (updatePopupVisible) return;

  const checker = createUpdateChecker();
  const hasUpdate = await checker.checkOnce();
  if (!hasUpdate) return;

  updatePopupVisible = true;

  // Build popup overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(28,28,26,0.35);
    display: flex; align-items: center; justify-content: center;
    padding: 1rem;
  `;

  const card = document.createElement('div');
  card.style.cssText = `
    background: var(--bg); border-radius: 12px;
    padding: 1.5rem; width: 100%; max-width: 320px;
    text-align: center;
  `;

  const title = document.createElement('h3');
  title.textContent = 'Update Available';
  title.style.cssText = 'color: var(--ink); margin: 0 0 1rem; font-size: 1.1rem; font-weight: 600;';

  const statusText = document.createElement('p');
  statusText.textContent = 'Downloading update...';
  statusText.style.cssText = 'color: var(--muted); font-size: 0.875rem; margin: 0 0 1rem;';

  // Progress-bar button: button-shaped element with a fill expanding inside it
  const progressBtn = document.createElement('button');
  progressBtn.disabled = true;
  progressBtn.style.cssText = `
    position: relative; width: 100%; height: 44px;
    background: var(--faint); color: #fff; border: none; border-radius: 8px;
    font-size: 1rem; font-weight: 600; cursor: pointer;
    font-family: inherit; overflow: hidden;
    pointer-events: none;
  `;

  const fill = document.createElement('div');
  fill.style.cssText = `
    position: absolute; inset: 0; width: 0%;
    background: var(--up); border-radius: 8px;
  `;

  const btnLabel = document.createElement('span');
  btnLabel.textContent = 'Updating...';
  btnLabel.style.cssText = 'position: relative; z-index: 1;';

  progressBtn.appendChild(fill);
  progressBtn.appendChild(btnLabel);

  const dismissLink = document.createElement('button');
  dismissLink.textContent = 'Dismiss';
  dismissLink.style.cssText = `
    background: none; border: none; color: var(--muted);
    font-size: 0.85rem; cursor: pointer; margin-top: 0.75rem;
    font-family: inherit;
  `;

  card.append(title, statusText, progressBtn, dismissLink);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const removePopup = () => {
    overlay.remove();
    updatePopupVisible = false;
  };

  dismissLink.addEventListener('click', removePopup);

  // Start simulated progress (ease toward ~90% over ~4s)
  let progress = 0;
  let swReady = false;
  const startTime = performance.now();

  function animateProgress() {
    if (swReady) return;
    const elapsed = (performance.now() - startTime) / 1000;
    // 1 - e^(-t) curve: fast start, approaches ~90% asymptotically
    progress = Math.min(0.9, 1 - Math.exp(-elapsed / 1.5)) * 100;
    fill.style.width = progress + '%';
    requestAnimationFrame(animateProgress);
  }
  requestAnimationFrame(animateProgress);

  // Trigger SW update
  try {
    await checker.applyAppUpdate();
    // SW activated — snap to 100% and enable restart
    swReady = true;
    fill.style.width = '100%';
    btnLabel.textContent = 'Restart';
    statusText.textContent = 'Update ready!';
    progressBtn.disabled = false;
    progressBtn.style.pointerEvents = 'auto';
    progressBtn.addEventListener('click', () => location.reload());
  } catch {
    // Timeout or error — show error state
    swReady = true;
    fill.style.width = '100%';
    fill.style.background = 'var(--down)';
    btnLabel.textContent = 'Retry';
    statusText.textContent = 'Update failed';
    progressBtn.disabled = false;
    progressBtn.style.pointerEvents = 'auto';
    progressBtn.addEventListener('click', () => {
      removePopup();
      checkForUpdate();
    });
  }
}
